import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { BudgetLine } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, deleteBudget, fetchBudgets, saveBudget } from "../services/expensesService";
import BudgetPeriodFilter from "../components/BudgetPeriodFilter";
import ExecutiveSummary from "../components/ExecutiveSummary";
import { ErrorBox, Field, Kpi, Page } from "../components/ui";
import { useBudgetPeriod } from "../hooks/useBudgetPeriod";
import { budgetPlanInsights } from "../lib/budgetInsights";
import { budgetPeriodToQuery } from "../lib/budgetPeriod";
import { BUDGETS_DASHBOARD_PATH, BUDGET_LINES_NEW_PARAM } from "../lib/budgetRoutes";
import { scopePeriods } from "../lib/budgetTimeline";
import { CATEGORY_COLORS, categoryLabel, categoryOptions, currentDay, currentMonth, dateTime, money, monthLabel } from "../lib/format";
import { ui } from "../theme";

// Presupuestos → Desglose (#613): captura y edición de las líneas de presupuesto
// (una por categoría y periodo, anual "2026" o mensual "2026-09") y su lista
// agrupada por periodo. El plan contra lo gastado se ve en el Dashboard
// (BudgetsSection); aquí solo se dice cuánto se planea.

const empty = { id: "", period: currentMonth().slice(0, 4), category: "plataformas", amount: "", notes: "", autoAdjust: true };

export default function BudgetLinesSection() {
  const { t, i18n } = useTranslation(["common", "expensesStatements"]);
  const tx = (k: string, o?: Record<string, unknown>) => t(`expensesStatements:budgetExec.${k}`, o);
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const period = useBudgetPeriod();
  const { range } = period;
  const [lines, setLines] = useState<BudgetLine[]>([]);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const periodRef = useRef<HTMLInputElement>(null);

  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const b = await fetchBudgets();
      if (id !== requestId.current) return;
      setLines(b);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err) || "load");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const showForm = useCallback((focus: boolean) => {
    formRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    if (focus) periodRef.current?.focus({ preventScroll: true });
  }, []);

  // «Nueva línea» del Dashboard llega con `?new=1`: formulario a la vista y con el foco.
  const wantsNew = params.get(BUDGET_LINES_NEW_PARAM) === "1";
  useEffect(() => {
    if (wantsNew) showForm(true);
  }, [wantsNew, showForm]);

  const submit = async () => {
    try {
      await saveBudget({ period: form.period, category: form.category as BudgetLine["category"], amount: Number(form.amount), notes: form.notes || null, autoAdjust: form.autoAdjust }, form.id || undefined);
      // Una línea guardada fuera del periodo del filtro no debe desaparecer de la
      // vista: el periodo se amplía hasta incluirla (#1332).
      const saved = form.period.length === 4 ? { from: `${form.period}-01`, to: `${form.period}-12` } : { from: form.period, to: form.period };
      const visible = form.period.length === 4 ? range.from.slice(0, 4) <= form.period && form.period <= range.to.slice(0, 4) : range.from <= form.period && form.period <= range.to;
      if (!visible) period.apply({ from: saved.from < range.from ? saved.from : range.from, to: saved.to > range.to ? saved.to : range.to });
      setForm({ ...empty, period: form.period });
      await load();
    } catch (err) {
      setError(apiErrorMessage(err) || "save");
    }
  };

  const periodName = (p: string) => (p.length === 4 ? `${t("expenses.budgets.fullYear")} ${p}` : monthLabel(p, i18n.language));
  const errorText = error === "load" ? t("expenses.errors.load") : error === "save" ? t("expenses.errors.save") : error;

  // Solo las líneas del periodo del filtro (#1332): las mensuales de esos meses y
  // las anuales de sus años, que se prorratean.
  const inRange = useMemo(() => {
    const years = new Set(scopePeriods(range).map((p) => p.slice(0, 4)));
    return lines.filter((l) => (l.period.length === 4 ? years.has(l.period) : l.period >= range.from && l.period <= range.to));
  }, [lines, range]);

  // Líneas agrupadas por periodo (más reciente primero), con su total.
  const grouped = useMemo(() => {
    const map = new Map<string, BudgetLine[]>();
    for (const l of inRange) map.set(l.period, [...(map.get(l.period) ?? []), l]);
    return [...map.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [inRange]);

  const plan = useMemo(() => budgetPlanInsights(lines, range, currentDay()), [lines, range]);
  const scope = range.from === range.to ? monthLabel(range.from, i18n.language) : `${monthLabel(range.from, i18n.language)} – ${monthLabel(range.to, i18n.language)}`;
  const fmt = (v: number) => money(v, "MXN", i18n.language);

  return (
    <Page
      title={t("expenses.budgets.linesTitle")}
      subtitle={t("expenses.budgets.linesSubtitle")}
      actions={
        <button type="button" className={c.secondary} onClick={() => navigate(`${BUDGETS_DASHBOARD_PATH}${budgetPeriodToQuery(range, currentMonth())}`)}>
          <i className="ri-arrow-left-line mr-1" />
          {t("expenses.budgets.backToDashboard")}
        </button>
      }
    >
      <BudgetPeriodFilter period={period} />

      <ErrorBox message={errorText} />

      <ExecutiveSummary title={tx("plan.title")} scope={scope} verdict={plan.verdict} findings={plan.findings} actions={plan.actions} testId="plan-summary">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="plan-kpis">
          <Kpi label={tx("plan.kpi.total")} value={fmt(plan.total)} hint={tx("plan.kpi.totalHint", { count: plan.months })} />
          <Kpi label={tx("plan.kpi.average")} value={plan.monthsCovered ? fmt(plan.monthlyAverage) : "—"} hint={tx("plan.kpi.averageHint")} />
          <Kpi label={tx("plan.kpi.covered")} value={`${plan.monthsCovered} / ${plan.months}`} hint={plan.gaps.length ? tx("plan.kpi.coveredGaps", { count: plan.gaps.length }) : tx("plan.kpi.coveredAll")} tone={plan.gaps.length ? "bad" : "good"} />
          <Kpi label={tx("plan.kpi.lines")} value={String(plan.auto + plan.pinned + plan.yearly)} hint={tx("plan.kpi.linesHint", { auto: plan.auto, pinned: plan.pinned, yearly: plan.yearly })} />
        </div>
        {plan.byCategory.length > 0 && (
          <div className={c.card} data-testid="plan-mix">
            <h3 className={`mb-2 text-sm font-semibold ${c.text}`}>{tx("plan.mixTitle")}</h3>
            <div className="flex h-3 w-full overflow-hidden rounded-full">
              {plan.byCategory.map((r) => (
                <div key={r.category} style={{ width: `${r.pct}%`, background: CATEGORY_COLORS[r.category] }} title={`${categoryLabel(r.category, i18n.language)} ${r.pct}%`} />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
              {plan.byCategory.map((r) => (
                <span key={r.category} className={`inline-flex items-center gap-1.5 text-xs ${c.text}`}>
                  <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_COLORS[r.category] }} />
                  {categoryLabel(r.category, i18n.language)}
                  <span className={`tabular-nums ${c.muted}`}>
                    {fmt(r.amount)} · {r.pct}%
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
      </ExecutiveSummary>

      <div ref={formRef} className={c.card} data-testid="budget-form">
        <h2 className={`text-sm font-semibold ${c.text}`}>{form.id ? t("expenses.budgets.editTitle") : t("expenses.budgets.formTitle")}</h2>
        <p className={`mb-3 text-xs ${c.muted}`}>{t("expenses.budgets.formHint")}</p>
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-5">
          <Field label={t("expenses.budgets.period")}>
            <input ref={periodRef} className={c.input} value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="2026 o 2026-09" />
          </Field>
          <Field label={t("expenses.fields.category")}>
            <select className={c.input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {categoryOptions(i18n.language, { include: form.category }).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("expenses.budgets.amount")}>
            <input type="number" min="0" className={c.input} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </Field>
          <Field label={t("expenses.fields.notes")}>
            <input className={c.input} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>
          <div className="flex gap-2">
            <button type="button" className={c.primary} disabled={!/^\d{4}(-\d{2})?$/.test(form.period) || form.amount === ""} onClick={submit}>
              {form.id ? t("expenses.save") : t("expenses.budgets.add")}
            </button>
            {form.id && (
              <button type="button" className={c.secondary} onClick={() => setForm(empty)}>
                {t("expenses.cancel")}
              </button>
            )}
          </div>
          <div className="sm:col-span-5">
            <label className={`inline-flex items-center gap-2 text-sm ${c.muted}`}>
              <input type="checkbox" className="h-4 w-4 accent-teal-600" checked={form.autoAdjust} onChange={(e) => setForm({ ...form, autoAdjust: e.target.checked })} />
              {t("expenses.budgets.auto.checkbox")}
            </label>
          </div>
        </div>
      </div>

      <div className={c.card} data-testid="budget-lines">
        <h2 className={`text-sm font-semibold ${c.text}`}>{t("expenses.budgets.lines")}</h2>
        <p className={`mb-2 text-xs ${c.muted}`}>{t("expenses.budgets.linesHint")}</p>
        {grouped.length === 0 && <p className={`py-3 text-center text-xs ${c.muted}`}>{t("expenses.budgets.noLines")}</p>}
        {lines.length > inRange.length && (
          <p className={`mb-2 text-xs ${c.muted}`} data-testid="lines-hidden">
            <i className="ri-filter-3-line mr-1" />
            {tx("plan.hidden", { count: lines.length - inRange.length })}
          </p>
        )}
        <div className="space-y-3">
          {grouped.map(([period, group]) => (
            <div key={period} className={`rounded-xl border ${c.border}`}>
              <div className={`flex items-center justify-between rounded-t-xl px-3 py-2 text-sm font-medium ${c.surface} ${c.text}`}>
                <span>{periodName(period)}</span>
                <span className="tabular-nums">{money(group.reduce((s, l) => s + l.amount, 0), "MXN", i18n.language)}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {group.map((l) => (
                    <tr key={l.id} className={`border-t ${c.border} ${c.text}`}>
                      <td className="w-56 px-3 py-1.5">
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ background: CATEGORY_COLORS[l.category] }} />
                          {categoryLabel(l.category, i18n.language)}
                        </span>
                      </td>
                      <td className="w-32 py-1.5 text-right tabular-nums">{money(l.amount, "MXN", i18n.language)}</td>
                      <td className={`px-3 py-1.5 text-xs ${c.muted}`}>
                        {l.period.length === 7 && (
                          <span
                            className={`mr-2 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${l.autoAdjust === false ? "bg-slate-500/15 text-slate-500" : "bg-teal-500/15 text-teal-600"}`}
                            title={l.adjustedAt ? t("expenses.budgets.auto.adjustedAt", { when: dateTime(l.adjustedAt, i18n.language) }) : undefined}
                          >
                            <i className={`mr-1 ${l.autoAdjust === false ? "ri-pushpin-line" : "ri-refresh-line"}`} />
                            {t(l.autoAdjust === false ? "expenses.budgets.auto.pinned" : "expenses.budgets.auto.auto")}
                          </span>
                        )}
                        {l.notes}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right">
                        <button
                          type="button"
                          aria-label={`edit-${l.id}`}
                          className="mr-3 text-teal-600"
                          onClick={() => {
                            setForm({ id: l.id, period: l.period, category: l.category, amount: String(l.amount), notes: l.notes || "", autoAdjust: l.autoAdjust !== false });
                            showForm(false);
                          }}
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button type="button" aria-label={`delete-${l.id}`} className="text-red-500" onClick={async () => { await deleteBudget(l.id); await load(); }}>
                          <i className="ri-delete-bin-line" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}
